/** @jsxRuntime classic */
/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
const React = require('react');
const { Helmet } = require('react-helmet-async');
const { Link, useLocation } = require('react-router-dom');
const { ArrowLeft, ArrowRight, Check, ExternalLink } = require('lucide-react');
const { guideRunbookForRoute } = require('../site/guidePresentation');
const { SITE_BASE_URL } = require('../site/siteRoutes');
const { getComparisonPage } = require('../site/comparisonContent');
const { structuredDataForRoute } = require('../site/structuredData');
const { ogImageUrlForPath } = require('../site/ogTitleCard');
const PrerenderReady = require('../components/PrerenderReady').default;
require('./ComparisonPage.css');

function InternalRouteAnchor({ children, className, to }) {
  return (
    <Link className={className} to={to}>
      {children}
    </Link>
  );
}

function ResourceAnchor({ item }) {
  const isExternal = /^https?:\/\//i.test(item.url);
  if (!isExternal) {
    return (
      <InternalRouteAnchor to={item.url}>
        <span>{item.label}</span>
        <ArrowRight size={16} aria-hidden="true" />
      </InternalRouteAnchor>
    );
  }

  return (
    <a href={item.url} target="_blank" rel="noreferrer">
      <span>{item.label}</span>
      <ExternalLink size={15} aria-hidden="true" />
    </a>
  );
}

function backLinkForPath(path) {
  if (path.startsWith('/brokers/')) return { label: 'All brokers', url: '/brokers' };
  if (path.startsWith('/compare/')) return { label: 'All comparisons', url: '/compare' };
  if (path.startsWith('/guides/')) {
    return { label: 'All trading guides', url: '/guides' };
  }
  return null;
}

function ComparisonPage() {
  const { pathname } = useLocation();
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const route = getComparisonPage(normalizedPathname);

  if (!route) {
    const isGuidePath =
      normalizedPathname === '/guides' || normalizedPathname.startsWith('/guides/');
    return (
      <section className="comparison-page comparison-page--missing bsui-scope">
        <h1>{isGuidePath ? 'Guide not found' : 'Comparison not found'}</h1>
        <InternalRouteAnchor to={isGuidePath ? '/guides' : '/compare'}>
          {isGuidePath ? 'Browse trading guides' : 'Browse platform comparisons'}
        </InternalRouteAnchor>
      </section>
    );
  }

  const canonical = `${SITE_BASE_URL}${route.path}`;
  const structuredData = structuredDataForRoute(route);
  const guideRunbook = guideRunbookForRoute(route);
  const backLink = guideRunbook ? null : backLinkForPath(route.path);
  const guideSlug = guideRunbook?.slug || null;
  const guideSections = guideRunbook?.sections || [];
  const guideNavItems = guideRunbook?.navItems || [];
  const primaryAction = route.content.primaryAction || {
    label: 'Start with BotSpot',
    url: '/sales?showLogin=1',
  };
  const secondaryAction = route.content.secondaryAction || {
    label: 'See MCP setup',
    url: '/agents',
  };

  return (
    <article className={`comparison-page${guideSlug ? ' comparison-page--guide' : ''} bsui-scope`}>
      {/* Content modules are bundled: settled for the build-time crawler snapshot. */}
      <PrerenderReady ready />
      <Helmet>
        <title>{route.title}</title>
        <meta name="description" content={route.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content={route.pageType === 'article' ? 'article' : 'website'} />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={route.title} />
        <meta property="og:description" content={route.description} />
        <meta property="og:image" content={ogImageUrlForPath(route.path)} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={route.title} />
        <meta name="twitter:description" content={route.description} />
        <meta name="twitter:image" content={ogImageUrlForPath(route.path)} />
        {structuredData.map((item, index) => (
          <script type="application/ld+json" key={`structured-data-${index}`}>
            {JSON.stringify(item)}
          </script>
        ))}
      </Helmet>

      <header className="comparison-page__hero">
        <div className="comparison-page__container">
          {backLink && (
            <InternalRouteAnchor className="comparison-page__back-link" to={backLink.url}>
              <ArrowLeft size={15} aria-hidden="true" />
              {backLink.label}
            </InternalRouteAnchor>
          )}
          <div className={guideSlug ? 'guide-hero__layout' : undefined}>
            <div>
              <h1>{route.content.heading}</h1>
              <p className="comparison-page__summary">{route.content.summary}</p>
              <div className="comparison-page__actions">
                <InternalRouteAnchor
                  className="comparison-page__primary-action"
                  to={primaryAction.url}
                >
                  {primaryAction.label}
                  <ArrowRight size={17} aria-hidden="true" />
                </InternalRouteAnchor>
                <InternalRouteAnchor
                  className="comparison-page__secondary-action"
                  to={secondaryAction.url}
                >
                  {secondaryAction.label}
                </InternalRouteAnchor>
              </div>
            </div>
            {guideRunbook && (
              <aside className="guide-hero__brief" aria-label="Guide brief">
                <p>Research brief</p>
                <dl>
                  <div>
                    <dt>Reviewed</dt>
                    <dd>
                      <time dateTime={guideRunbook.reviewedAt}>{guideRunbook.reviewedAt}</time>
                    </dd>
                  </div>
                  <div>
                    <dt>Coverage</dt>
                    <dd>{guideSections.length} sections</dd>
                  </div>
                  <div>
                    <dt>Evidence</dt>
                    <dd>{route.content.sources?.length || 0} sources</dd>
                  </div>
                </dl>
              </aside>
            )}
          </div>
          {!guideRunbook && route.content.proofPoints && (
            <div className="comparison-page__proof-grid" aria-label="BotSpot capabilities">
              {route.content.proofPoints.map((point) => (
                <section key={point.title}>
                  <h2>{point.title}</h2>
                  <p>{point.text}</p>
                </section>
              ))}
            </div>
          )}
        </div>
      </header>

      <div className="comparison-page__body comparison-page__container">
        <div className="comparison-page__content">
          {route.content.comparisonRows && (
            <section className="comparison-page__section comparison-page__comparison">
              <h2>Capability comparison</h2>
              <div className="comparison-page__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Capability</th>
                      <th scope="col">BotSpot</th>
                      <th scope="col">Other platform or broker</th>
                    </tr>
                  </thead>
                  <tbody>
                    {route.content.comparisonRows.map((row) => (
                      <tr key={row.capability}>
                        <th scope="row">{row.capability}</th>
                        <td>{row.botspot}</td>
                        <td>{row.alternative}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {guideSlug
            ? guideSections.map(({ heading, id, indexLabel, section }) => (
                <section
                  className="comparison-page__section guide-section"
                  id={id}
                  key={section.heading}
                >
                  <span className="guide-section__index" aria-hidden="true">
                    {indexLabel}
                  </span>
                  <div className="guide-section__content">
                    <h2>{heading}</h2>
                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.links?.map((link) => (
                      <p key={link.url}>
                        <Link to={link.url}>{link.label}</Link>
                      </p>
                    ))}
                    {section.bullets && (
                      <ul>
                        {section.bullets.map((bullet) => (
                          <li key={bullet}>
                            <Check size={17} aria-hidden="true" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              ))
            : route.content.sections.map((section) => (
                <section className="comparison-page__section" key={section.heading}>
                  <h2>{section.heading}</h2>
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.bullets && (
                    <ul>
                      {section.bullets.map((bullet) => (
                        <li key={bullet}>
                          <Check size={17} aria-hidden="true" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

          {route.content.cards && (
            <section
              className="comparison-page__section"
              id={guideSlug ? 'guide-related-paths' : undefined}
            >
              <h2>{guideSlug ? 'Explore related paths' : 'Explore the right path'}</h2>
              <div className="comparison-page__card-grid">
                {route.content.cards.map((card) => (
                  <InternalRouteAnchor
                    className="comparison-page__card"
                    to={card.url}
                    key={card.url}
                  >
                    <h3>{card.heading}</h3>
                    <p>{card.summary}</p>
                    <strong>
                      Explore
                      <ArrowRight size={16} aria-hidden="true" />
                    </strong>
                  </InternalRouteAnchor>
                ))}
              </div>
            </section>
          )}

          {route.content.faq && (
            <section
              className="comparison-page__section comparison-page__faq"
              id={guideSlug ? 'guide-frequently-asked-questions' : undefined}
            >
              <h2>Frequently asked questions</h2>
              {route.content.faq.map((item) => (
                <article key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </section>
          )}
        </div>

        <aside className="comparison-page__sidebar">
          {guideNavItems.length > 0 && (
            <nav className="comparison-page__resource-panel guide-toc" aria-label="On this page">
              <h2>On this page</h2>
              <ol>
                {guideNavItems.map((item, index) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      {item.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}
          {route.content.relatedLinks && (
            <nav className="comparison-page__resource-panel" aria-label="Related BotSpot resources">
              <h2>Related resources</h2>
              <ul>
                {route.content.relatedLinks.map((item) => (
                  <li key={item.url}>
                    <ResourceAnchor item={item} />
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {route.content.sources && (
            <section className="comparison-page__resource-panel">
              <h2>Sources</h2>
              <ul>
                {route.content.sources.map((item) => (
                  <li key={item.url}>
                    <ResourceAnchor item={item} />
                  </li>
                ))}
              </ul>
              {route.content.verifiedAt && (
                <p className="comparison-page__verified">
                  Sources verified {route.content.verifiedAt}.
                </p>
              )}
            </section>
          )}
        </aside>
      </div>
    </article>
  );
}

module.exports = ComparisonPage;
